import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import toast, { Toaster } from "react-hot-toast";
import {
  fetchManagement,
  addManagement,
  updateManagement,
  deleteManagement,
  fetchPermissions,
} from "../../redux/managementSlice";
import { fetchAdminStaff } from "../../redux/administratorSlice";
import {
  initiateGoalPlanning,
  fetchGoalPlanningsByEmail,
  submitConferenceGoalPlanning,
} from "../../redux/goalPlanningSlice";
import {
  initiateAdminCheckIn,
  fetchAdminCheckInsByEmail,
  submitConferenceCheckIn,
} from "../../redux/adminCheckInSlice";
import {
  initiateAdminSiteVisit,
  fetchSiteVisitsByEmail,
  submitObservationsSiteVisit,
  submitFeedbackSiteVisit,
} from "../../redux/siteVisitSlice";
import { fetchCampuses } from "../../redux/campusesSlice";
import ManagementModal from "../../modals/admin/ManagementModel";
import GoalPlanningModal from "../../modals/admin/GoalPlanningModal";
import AdminCheckInModal from "../../modals/admin/AdminCheckInModal";
import SiteVisitModal from "../../modals/admin/SiteVisitModal";
import CustomDropdown from "../../Components/admin/CustomDropdown";
import AnnualGoalsAdminList from "../../Components/admin/AnnualGoalsAdminList";
import SiteVisitsAdminList from "../../Components/admin/SiteVisitsAdminList";
import { Pencil, Trash2, Plus } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { hrmsGetRequest } from "../../services";

const TABS = {
  ADMINISTRATOR: "Administrator",
  ANNUAL_GOALS: "Annual Goals",
  SITE_VISITS: "Site Visits / Evaluation",
};

const Management = () => {
  const dispatch = useDispatch();
  const { management } = useSelector((state) => state.management);
  const { campuses } = useSelector((state) => state.campuses);
  const { register, handleSubmit, reset, setValue } = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentManagementId, setCurrentManagementId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const { permissions } = useSelector((state) => state.management);
  const { adminStaff, loading: adminStaffLoading } = useSelector(
    (state) => state.administrators || { adminStaff: [], loading: false }
  );
  const { goalPlannings, loading: goalPlanningLoading } = useSelector(
    (state) => state.goalPlanning || { goalPlannings: [], loading: false }
  );
  const { adminCheckIns, loading: checkInLoading } = useSelector(
    (state) => state.adminCheckIn || { adminCheckIns: [], loading: false }
  );
  const { siteVisits, loading: siteVisitLoading } = useSelector(
    (state) => state.siteVisit || { siteVisits: [], loading: false }
  );
  const auth = JSON.parse(localStorage.getItem("user") || "{}");
  const getSuperAdminEmail = () => {
    try {
      if (auth.email) return auth.email;
      if (auth.token) {
        const decoded = jwtDecode(auth.token);
        return decoded.email || decoded.unique_name || "";
      }
    } catch {
      return "";
    }
    return "";
  };
  const [activeTab, setActiveTab] = useState(TABS.ADMINISTRATOR);
  const [annualGoalsAdminId, setAnnualGoalsAdminId] = useState("");
  const [siteVisitsAdminId, setSiteVisitsAdminId] = useState("");
  const [isGoalPlanningModalOpen, setIsGoalPlanningModalOpen] = useState(false);
  const [goalPlanningModalMode, setGoalPlanningModalMode] = useState("initiate");
  const [selectedGoalPlanning, setSelectedGoalPlanning] = useState(null);
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInModalMode, setCheckInModalMode] = useState("initiate");
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [selectedCheckInType, setSelectedCheckInType] = useState("mid_year");
  const [isSiteVisitModalOpen, setIsSiteVisitModalOpen] = useState(false);
  const [siteVisitModalMode, setSiteVisitModalMode] = useState("initiate");
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [annualStatusByEmail, setAnnualStatusByEmail] = useState({});
  const [annualStatusesLoading, setAnnualStatusesLoading] = useState(false);
  const [siteVisitStatusByEmail, setSiteVisitStatusByEmail] = useState({});
  const [siteVisitStatusesLoading, setSiteVisitStatusesLoading] = useState(false);
  const tabs = Object.values(TABS);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    dispatch(fetchManagement());
    dispatch(fetchCampuses());
    dispatch(fetchPermissions());
    dispatch(fetchAdminStaff());
  }, [dispatch]);

  const onSubmit = (data) => {
    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }

    const selectedPermissions = Object.keys(data)
      .filter((key) => key.startsWith("permission_") && data[key])
      .map((key) => key.replace("permission_", ""));

    const newManagement = {
      ...data,
      permissions: selectedPermissions,
      campusIds: data.campusIds || [],
      id: isEdit ? currentManagementId : Date.now(),
    };

    if (isEdit) {
      dispatch(updateManagement({ id: currentManagementId, management: newManagement })).then(() => dispatch(fetchManagement()));
    } else {
      dispatch(addManagement(newManagement)).then(() => {
        dispatch(fetchManagement());
      });
      toast.success("Management added successfully!");
    }

    reset();
    setValue("campuses", []);
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const handleEdit = (id) => {
    const managementItem = management.find((item) => item.id === id);
    if (!managementItem) return;

    permissions.forEach((perm) => setValue(`permission_${perm.name}`, false));

    setValue("firstName", managementItem.firstName);
    setValue("lastName", managementItem.lastName);
    setValue("email", managementItem.email);
    setValue("department", managementItem.department);

    managementItem.permissions.forEach((permission) => {
      setValue(`permission_${permission}`, true);
    });

    setCurrentManagementId(id);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteManagement(id));
    toast.success("Management deleted successfully!");
  };

  const filteredManagement = management.filter((item) => {
    const matchesSearch = `${item.firstName} ${item.lastName}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesLocation = locationFilter
      ? item.location === locationFilter
      : true;

    return matchesSearch && matchesLocation;
  });

  const uniqueLocations = Array.from(
    new Set(management.map((item) => item.location))
  );

  const loadAnnualGoalsStatuses = async () => {
    if (!adminStaff.length) {
      setAnnualStatusByEmail({})
      return
    }

    setAnnualStatusesLoading(true)
    try {
      const entries = await Promise.all(
        adminStaff.map(async (admin) => {
          const email = admin.email
          try {
            const [goalRes, checkInRes] = await Promise.all([
              hrmsGetRequest(`/api/goal-planning/email/${encodeURIComponent(email)}`),
              hrmsGetRequest(
                `/api/admin-check-ins/email/${encodeURIComponent(email)}`
              ),
            ])
            return [
              email.toLowerCase(),
              {
                goalPlannings: goalRes.data || [],
                checkIns: checkInRes.data || [],
              },
            ]
          } catch {
            return [
              email.toLowerCase(),
              { goalPlannings: [], checkIns: [] },
            ]
          }
        })
      )
      setAnnualStatusByEmail(Object.fromEntries(entries))
    } finally {
      setAnnualStatusesLoading(false)
    }
  }

  const loadSiteVisitStatuses = async () => {
    if (!adminStaff.length) {
      setSiteVisitStatusByEmail({})
      return
    }

    setSiteVisitStatusesLoading(true)
    try {
      const entries = await Promise.all(
        adminStaff.map(async (admin) => {
          const email = admin.email
          try {
            const response = await hrmsGetRequest(
              `/api/admin-site-visits/email/${encodeURIComponent(email)}`
            )
            return [
              email.toLowerCase(),
              { siteVisits: response.data || [] },
            ]
          } catch {
            return [email.toLowerCase(), { siteVisits: [] }]
          }
        })
      )
      setSiteVisitStatusByEmail(Object.fromEntries(entries))
    } finally {
      setSiteVisitStatusesLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab === TABS.ANNUAL_GOALS && adminStaff.length > 0) {
      loadAnnualGoalsStatuses()
    }
  }, [activeTab, adminStaff])

  useEffect(() => {
    if (activeTab === TABS.SITE_VISITS && adminStaff.length > 0) {
      loadSiteVisitStatuses()
    }
  }, [activeTab, adminStaff])

  const selectedAnnualGoalsAdmin = adminStaff.find(
    (admin) => String(admin.id) === String(annualGoalsAdminId)
  );

  const selectedSiteVisitsAdmin = adminStaff.find(
    (admin) => String(admin.id) === String(siteVisitsAdminId)
  );

  useEffect(() => {
    if (selectedAnnualGoalsAdmin?.email) {
      dispatch(fetchGoalPlanningsByEmail(selectedAnnualGoalsAdmin.email));
      dispatch(fetchAdminCheckInsByEmail(selectedAnnualGoalsAdmin.email));
    }
  }, [dispatch, selectedAnnualGoalsAdmin?.email]);

  useEffect(() => {
    if (selectedSiteVisitsAdmin?.email) {
      dispatch(fetchSiteVisitsByEmail(selectedSiteVisitsAdmin.email));
    }
  }, [dispatch, selectedSiteVisitsAdmin?.email]);

  const getStatusLabel = (status) => {
    if (status === "initiated") return "Pending Admin Fill";
    if (status === "submitted_by_admin") return "Awaiting Conference Review";
    if (status === "completed") return "Completed";
    return status;
  };

  const getStatusClass = (status) => {
    if (status === "initiated") return "bg-yellow-100 text-yellow-800";
    if (status === "submitted_by_admin") return "bg-blue-100 text-blue-800";
    if (status === "completed") return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const getSiteVisitStatusLabel = (status) => {
    if (status === "initiated") return "Awaiting Observation Evidence";
    if (status === "observations_captured") return "Pending Admin Post-Observation";
    if (status === "submitted_by_admin") return "Awaiting Evaluation";
    if (status === "completed") return "Completed (Locked)";
    return status;
  };

  const getSiteVisitStatusClass = (status) => {
    if (status === "initiated") return "bg-yellow-100 text-yellow-800";
    if (status === "observations_captured") return "bg-orange-100 text-orange-800";
    if (status === "submitted_by_admin") return "bg-blue-100 text-blue-800";
    if (status === "completed") return "bg-green-100 text-green-800";
    return "bg-gray-100 text-gray-800";
  };

  const handleOpenInitiateModal = (admin) => {
    if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
    setSelectedGoalPlanning(null);
    setGoalPlanningModalMode("initiate");
    setIsGoalPlanningModalOpen(true);
  };

  const handleInitiateGoalPlanning = async () => {
    if (!selectedAnnualGoalsAdmin) return;

    try {
      await dispatch(
        initiateGoalPlanning({
          adminEmail: selectedAnnualGoalsAdmin.email,
          adminName: `${selectedAnnualGoalsAdmin.firstName} ${selectedAnnualGoalsAdmin.lastName}`,
          initiatedByEmail: getSuperAdminEmail(),
        })
      ).unwrap();

      setIsGoalPlanningModalOpen(false);
      dispatch(fetchGoalPlanningsByEmail(selectedAnnualGoalsAdmin.email));
      loadAnnualGoalsStatuses();
    } catch {
      // Error toast handled in slice
    }
  };

  const handleOpenConferenceModal = (admin, goalPlanning) => {
    if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
    setSelectedGoalPlanning(goalPlanning);
    setGoalPlanningModalMode("super_admin_conference");
    setIsGoalPlanningModalOpen(true);
  };

  const handleConferenceSubmit = async (conferenceData) => {
    if (!selectedGoalPlanning?.id) return;

    try {
      await dispatch(
        submitConferenceGoalPlanning({
          id: selectedGoalPlanning.id,
          formData: conferenceData,
        })
      ).unwrap();

      setIsGoalPlanningModalOpen(false);
      if (selectedAnnualGoalsAdmin?.email) {
        dispatch(fetchGoalPlanningsByEmail(selectedAnnualGoalsAdmin.email));
      }
      loadAnnualGoalsStatuses();
    } catch {
      // Error toast handled in slice
    }
  };

  const getCompletedGoalPlanningForAdmin = (adminEmail) => {
    const data = annualStatusByEmail[adminEmail?.toLowerCase()] || {
      goalPlannings: [],
    };
    return data.goalPlannings.find(
      (gp) => gp.status === "completed" && gp.year === currentYear
    );
  };

  const handleOpenCheckInInitiate = (admin, checkInType) => {
    if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
    setSelectedCheckInType(checkInType);
    setSelectedCheckIn(null);
    setCheckInModalMode("initiate");
    setIsCheckInModalOpen(true);
  };

  const handleInitiateCheckIn = async () => {
    if (!selectedAnnualGoalsAdmin) return;

    try {
      await dispatch(
        initiateAdminCheckIn({
          adminEmail: selectedAnnualGoalsAdmin.email,
          adminName: `${selectedAnnualGoalsAdmin.firstName} ${selectedAnnualGoalsAdmin.lastName}`,
          initiatedByEmail: getSuperAdminEmail(),
          checkInType: selectedCheckInType,
          goalPlanningId: getCompletedGoalPlanningForAdmin(
            selectedAnnualGoalsAdmin.email
          )?.id,
        })
      ).unwrap();

      setIsCheckInModalOpen(false);
      dispatch(fetchAdminCheckInsByEmail(selectedAnnualGoalsAdmin.email));
      loadAnnualGoalsStatuses();
    } catch {
      // handled in slice
    }
  };

  const handleOpenCheckInConference = (admin, checkIn) => {
    if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
    setSelectedCheckIn(checkIn);
    setSelectedCheckInType(checkIn.checkInType);
    setCheckInModalMode("super_admin_conference");
    setIsCheckInModalOpen(true);
  };

  const handleCheckInConferenceSubmit = async (conferenceData) => {
    if (!selectedCheckIn?.id) return;

    try {
      await dispatch(
        submitConferenceCheckIn({
          id: selectedCheckIn.id,
          formData: conferenceData,
        })
      ).unwrap();

      setIsCheckInModalOpen(false);
      if (selectedAnnualGoalsAdmin?.email) {
        dispatch(fetchAdminCheckInsByEmail(selectedAnnualGoalsAdmin.email));
      }
      loadAnnualGoalsStatuses();
    } catch {
      // handled in slice
    }
  };

  const handleOpenSiteVisitInitiate = (admin) => {
    if (admin?.id) setSiteVisitsAdminId(String(admin.id));
    setSelectedSiteVisit(null);
    setSiteVisitModalMode("initiate");
    setIsSiteVisitModalOpen(true);
  };

  const handleInitiateSiteVisit = async (initData) => {
    if (!selectedSiteVisitsAdmin) return;

    try {
      await dispatch(
        initiateAdminSiteVisit({
          adminEmail: selectedSiteVisitsAdmin.email,
          adminName: `${selectedSiteVisitsAdmin.firstName} ${selectedSiteVisitsAdmin.lastName}`,
          initiatedByEmail: getSuperAdminEmail(),
          ...initData,
        })
      ).unwrap();

      setIsSiteVisitModalOpen(false);
      dispatch(fetchSiteVisitsByEmail(selectedSiteVisitsAdmin.email));
      loadSiteVisitStatuses();
    } catch {
      // handled in slice
    }
  };

  const handleOpenSiteVisitEvaluation = (admin, siteVisit) => {
    if (admin?.id) setSiteVisitsAdminId(String(admin.id));
    setSelectedSiteVisit(siteVisit);
    setSiteVisitModalMode("super_admin_evaluator");
    setIsSiteVisitModalOpen(true);
  };

  const handleOpenCaptureObservations = (admin, siteVisit) => {
    if (admin?.id) setSiteVisitsAdminId(String(admin.id));
    setSelectedSiteVisit(siteVisit);
    setSiteVisitModalMode("capture_observations");
    setIsSiteVisitModalOpen(true);
  };

  const handleSiteVisitObservationsSubmit = async (formData) => {
    if (!selectedSiteVisit?.id) return;

    try {
      await dispatch(
        submitObservationsSiteVisit({
          id: selectedSiteVisit.id,
          formData,
        })
      ).unwrap();

      setIsSiteVisitModalOpen(false);
      if (selectedSiteVisitsAdmin?.email) {
        dispatch(fetchSiteVisitsByEmail(selectedSiteVisitsAdmin.email));
      }
      loadSiteVisitStatuses();
    } catch {
      // handled in slice
    }
  };

  const handleSiteVisitFeedbackSubmit = async (formData) => {
    if (!selectedSiteVisit?.id) return;

    try {
      await dispatch(
        submitFeedbackSiteVisit({
          id: selectedSiteVisit.id,
          formData,
        })
      ).unwrap();

      setIsSiteVisitModalOpen(false);
      if (selectedSiteVisitsAdmin?.email) {
        dispatch(fetchSiteVisitsByEmail(selectedSiteVisitsAdmin.email));
      }
      loadSiteVisitStatuses();
    } catch {
      // handled in slice
    }
  };

  return (
    <div className="container mx-auto p-4 border border-[#241763]">
      <div className="flex space-x-4 border-2 border-gray-100 shadow-xl mb-4 pb-3 pt-2 rounded-b-xl">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 ${
              activeTab === tab
                ? "text-white bg-[#241763]"
                : "bg-gray-200 text-gray-700"
            } rounded-t-md`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === TABS.ADMINISTRATOR && (
        <>
          <div className="mb-4 flex flex-wrap gap-4">
            <div className="flex flex-grow items-end">
              <input
                type="text"
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2 border rounded"
              />
            </div>

            <div>
              <CustomDropdown
                options={uniqueLocations}
                value={locationFilter}
                onChange={setLocationFilter}
                placeholder="All locations"
              />
            </div>

            <div className="w-full md:w-1/2 lg:w-auto">
              <button
                className="w-full lg:w-auto flex items-center justify-center bg-[#241763] text-white p-2 rounded"
                onClick={() => {
                  setIsModalOpen(true);
                  setIsEdit(false);
                  reset();
                }}
              >
                <Plus className="h-4 w-4" />
                <span className="ml-2">Add Admin</span>
              </button>
            </div>
          </div>

          <ManagementModal
            isModalOpen={isModalOpen}
            isEdit={isEdit}
            onSubmit={onSubmit}
            handleSubmit={handleSubmit}
            setIsModalOpen={setIsModalOpen}
            register={register}
            reset={reset}
            setValue={setValue}
            options={permissions}
            campuses={campuses}
            selectedCampuses={
              isEdit
                ? management.find((item) => item.id === currentManagementId)
                    ?.campuses || []
                : []
            }
          />

          <table className="table-auto w-full mt-6">
            <thead className="py-4 border-b font-bold">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>User Role</th>
                <th>Permissions</th>
                <th>Campuses</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filteredManagement.map((parent) => (
                <tr
                  key={parent.id}
                  className="p-2 py-2 hover:bg-gray-200 space-y-2"
                >
                  <td>{`${parent.firstName} ${parent.lastName}`}</td>
                  <td>{parent.email}</td>
                  <td>{parent.userRole}</td>
                  <td>
                    {parent.permissions
                      ? parent.permissions.join(", ")
                      : "N/A"}
                  </td>

                  <td>
                    {parent.campuses && parent.campuses.length > 0
                      ? parent.campuses
                          .map((campus) => campus.name)
                          .join(", ")
                      : "N/A"}
                  </td>

                  <td className="space-x-2 flex items-center py-2">
                    <button
                      className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                      onClick={() => handleEdit(parent.id)}
                    >
                      <Pencil className="text-[#241763] w-4 h-4 group-hover:text-white" />
                    </button>
                    <button
                      className="bg-[#B6A5C9]/50 text-white p-2 rounded hover:bg-[#241763]/60 group"
                      onClick={() => handleDelete(parent.id)}
                    >
                      <Trash2 className="text-[#241763] w-4 h-4 group-hover:text-white" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {activeTab === TABS.ANNUAL_GOALS && (
        <div className="space-y-6">
          <AnnualGoalsAdminList
            adminStaff={adminStaff}
            loading={adminStaffLoading}
            statusByEmail={annualStatusByEmail}
            statusesLoading={annualStatusesLoading}
            currentYear={currentYear}
            getStatusLabel={getStatusLabel}
            getStatusClass={getStatusClass}
            onInitiateGoalPlanning={handleOpenInitiateModal}
            onInitiateMidYear={(admin) =>
              handleOpenCheckInInitiate(admin, "mid_year")
            }
            onInitiateEndYear={(admin) =>
              handleOpenCheckInInitiate(admin, "end_of_year")
            }
            onOpenConference={handleOpenConferenceModal}
            onOpenCheckInConference={handleOpenCheckInConference}
            onViewGoalPlanning={(admin, goalPlanning) => {
              if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
              setSelectedGoalPlanning(goalPlanning);
              setGoalPlanningModalMode("view");
              setIsGoalPlanningModalOpen(true);
            }}
            onViewCheckIn={(admin, checkIn) => {
              if (admin?.id) setAnnualGoalsAdminId(String(admin.id));
              setSelectedCheckIn(checkIn);
              setSelectedCheckInType(checkIn.checkInType);
              setCheckInModalMode("view");
              setIsCheckInModalOpen(true);
            }}
          />
        </div>
      )}

      {activeTab === TABS.SITE_VISITS && (
        <div className="space-y-6">
          <SiteVisitsAdminList
            adminStaff={adminStaff}
            loading={adminStaffLoading}
            statusByEmail={siteVisitStatusByEmail}
            statusesLoading={siteVisitStatusesLoading}
            currentYear={currentYear}
            getStatusLabel={getSiteVisitStatusLabel}
            getStatusClass={getSiteVisitStatusClass}
            onInitiateSiteVisit={handleOpenSiteVisitInitiate}
            onCaptureObservations={handleOpenCaptureObservations}
            onCompleteEvaluation={handleOpenSiteVisitEvaluation}
            onViewSiteVisit={(admin, siteVisit) => {
              if (admin?.id) setSiteVisitsAdminId(String(admin.id));
              setSelectedSiteVisit(siteVisit);
              setSiteVisitModalMode("view");
              setIsSiteVisitModalOpen(true);
            }}
          />
        </div>
      )}

      <GoalPlanningModal
        isOpen={isGoalPlanningModalOpen}
        onClose={() => setIsGoalPlanningModalOpen(false)}
        mode={goalPlanningModalMode}
        adminName={
          selectedAnnualGoalsAdmin
            ? `${selectedAnnualGoalsAdmin.firstName} ${selectedAnnualGoalsAdmin.lastName}`
            : ""
        }
        goalPlanning={selectedGoalPlanning}
        onInitiate={handleInitiateGoalPlanning}
        onConferenceSubmit={handleConferenceSubmit}
        loading={goalPlanningLoading}
      />

      <AdminCheckInModal
        isOpen={isCheckInModalOpen}
        onClose={() => setIsCheckInModalOpen(false)}
        mode={checkInModalMode}
        checkInType={selectedCheckInType}
        adminName={
          selectedAnnualGoalsAdmin
            ? `${selectedAnnualGoalsAdmin.firstName} ${selectedAnnualGoalsAdmin.lastName}`
            : ""
        }
        checkIn={selectedCheckIn}
        onInitiate={handleInitiateCheckIn}
        onConferenceSubmit={handleCheckInConferenceSubmit}
        loading={checkInLoading}
      />

      <SiteVisitModal
        isOpen={isSiteVisitModalOpen}
        onClose={() => setIsSiteVisitModalOpen(false)}
        mode={siteVisitModalMode}
        adminName={
          selectedSiteVisitsAdmin
            ? `${selectedSiteVisitsAdmin.firstName} ${selectedSiteVisitsAdmin.lastName}`
            : ""
        }
        siteVisit={selectedSiteVisit}
        onInitiate={handleInitiateSiteVisit}
        onObservationsSubmit={handleSiteVisitObservationsSubmit}
        onFeedbackSubmit={handleSiteVisitFeedbackSubmit}
        loading={siteVisitLoading}
      />

      <Toaster />
    </div>
  );
};

export default Management;
